/**
 * Registration Detail Page - Participant Onboarding Lifecycle
 *
 * Deep-dive into a single registration with document verification,
 * identity profiling, and cohort assignment.
 * Built with Operational Architect aesthetic: precision, editorial flow.
 */
import { useParams, Link, useNavigate } from 'react-router';
import {
  ChevronRight,
  CheckCircle,
  User,
  Mail,
  Phone,
  GraduationCap,
  FileText,
  Clock,
  MessageSquare,
  ShieldCheck,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { Button, Card, StatusChip } from '@lms/ui';
import {
  useRegistrationDetail,
  useApproveRegistration,
  useRejectRegistration,
  useAssignWhatsAppGroup,
  isIncompleteUserProfileError,
} from '../hooks/useRegistrations';
import { useWhatsAppGroups } from '../hooks/useWhatsAppGroups';
import { useAuthStore } from '../store/auth';
import { useState } from 'react';
import { ReceiptModal } from '../components/ReceiptModal';
import { RejectionModal } from '../components/RejectionModal';
import { EditUserModal, type MissingProfileField } from '../components/EditUserModal';
import { Download as DownloadIcon } from 'lucide-react';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import { useLanguage } from '../lib/LanguageContext';

export function RegistrationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: admin } = useAuthStore();
  const { language, t } = useLanguage();
  const { data: registration, isLoading, error } = useRegistrationDetail(id || '');

  const approveMutation = useApproveRegistration();
  const rejectMutation = useRejectRegistration();
  const assignMutation = useAssignWhatsAppGroup();
  const { data: waGroups } = useWhatsAppGroups({ programId: registration?.programId });

  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [syncedGroupId, setSyncedGroupId] = useState<string | undefined>(undefined);
  const [adminNotes, setAdminNotes] = useState('');
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  // Auto-recovery state for /approve → 409 "Incomplete User Profile".
  const [editUserContext, setEditUserContext] = useState<{
    userId: string;
    registrationId: string;
    missingFields: MissingProfileField[];
    reason: string;
  } | null>(null);

  // Sync selected group when registration data loads or changes
  const currentGroupId = registration?.participantId?.whatsappGroupId;
  if (currentGroupId !== syncedGroupId) {
    setSyncedGroupId(currentGroupId);
    setSelectedGroupId(currentGroupId || '');
  }

  const handleApprove = () => {
    if (!id || !admin) return;
    approveMutation.mutate(
      {
        id,
        adminNotes: adminNotes || 'Criteria met',
      },
      {
        onSuccess: () => {
          void navigate('/registrations');
        },
        onError: err => {
          if (isIncompleteUserProfileError(err)) {
            setEditUserContext({
              userId: err.body.userId,
              registrationId: err.body.registrationId,
              missingFields: err.body.missingFields,
              reason: err.body?.message || err.message,
            });
          }
        },
      }
    );
  };

  const handleReject = (reason: string) => {
    if (!id || !admin) return;
    rejectMutation.mutate(
      {
        id,
        adminNotes: reason || 'Did not meet criteria',
      },
      {
        onSuccess: () => {
          setIsRejectionModalOpen(false);
          void navigate('/registrations');
        },
      }
    );
  };

  const handleAssign = () => {
    if (!id || !selectedGroupId) return;
    assignMutation.mutate({ id, whatsappGroupId: selectedGroupId });
  };

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );

  if (error || !registration)
    return (
      <div className="p-8 bg-error-container/10 border border-error/20 rounded-2xl text-center">
        <AlertCircle className="text-error mx-auto mb-4" size={48} />
        <h2 className="text-xl font-headline font-bold text-error">
          <Bilingual {...copy.registrationDetail.emptyTitle} />
        </h2>
        <p className="text-on-surface-variant mt-2">
          <Bilingual {...copy.registrationDetail.emptyDesc} />
        </p>
        <Link
          to="/registrations"
          className="mt-6 inline-block text-primary font-bold hover:underline"
        >
          <Bilingual {...copy.registrationDetail.returnToList} />
        </Link>
      </div>
    );

  return (
    <div className="space-y-12 animate-fadeIn max-w-6xl mx-auto">
      {/* Editorial Header */}
      <section className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 pb-8 border-b border-outline-variant/10">
        <div>
          <div className="flex items-center gap-2 text-on-surface-variant text-[10px] font-label font-bold uppercase tracking-widest mb-6">
            <span>
              <Bilingual {...copy.sidebar.registrations} />
            </span>
            <ChevronRight size={12} />
            <span>
              <Bilingual {...copy.registrationDetail.breadcrumbDossier} />
            </span>
            <ChevronRight size={12} />
            <span className="text-primary font-black">{registration.user?.name}</span>
          </div>
          <h1 className="text-4xl font-headline font-extrabold text-on-surface tracking-tight m-0 mb-3">
            {registration.user?.name}
          </h1>
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 bg-surface-container-high text-on-surface-variant font-mono font-bold rounded-lg text-[10px] tracking-widest border border-outline-variant/20 uppercase">
              ID: {registration.id.slice(-8)}
            </span>
            <StatusChip
              label={
                registration.status === 'PENDING'
                  ? t(copy.registrations.filterPending)
                  : registration.status === 'APPROVED'
                    ? t(copy.registrations.filterApproved)
                    : registration.status === 'REJECTED'
                      ? t(copy.registrations.filterRejected)
                      : registration.status === 'REVISION'
                        ? t(copy.registrations.filterRevision)
                        : registration.status
              }
              variant={
                registration.status === 'APPROVED'
                  ? 'success'
                  : registration.status === 'REJECTED'
                    ? 'error'
                    : registration.status === 'REVISION'
                      ? 'warning'
                      : 'neutral'
              }
            />
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button
            onClick={() => setIsRejectionModalOpen(true)}
            variant="outline"
            size="sm"
            className="px-6 text-error border-error/50 hover:bg-error/5 hover:text-error transition-colors"
            disabled={rejectMutation.isPending || registration.status !== 'PENDING'}
          >
            {rejectMutation.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Bilingual {...copy.registrations.btnReject} />
            )}
          </Button>
          <Button
            onClick={handleApprove}
            size="sm"
            className="px-8 shadow-level2"
            disabled={approveMutation.isPending || registration.status !== 'PENDING'}
          >
            {approveMutation.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Bilingual {...copy.registrationDetail.btnAuthorize} />
            )}
          </Button>
        </div>
      </section>

      {/* Main Dossier Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
        {/* Dossier Content (Span 8) */}
        <div className="lg:col-span-8 space-y-10">
          {/* Identity Profile */}
          <Card className="border border-on-surface/5 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-primary" />
            <div className="flex justify-between items-start mb-10">
              <div>
                <h3 className="text-xl font-headline font-bold text-on-surface m-0">
                  <Bilingual {...copy.registrationDetail.sectionProfile} />
                </h3>
                <p className="text-xs font-body text-on-surface-variant mt-1">
                  <Bilingual {...copy.registrationDetail.profileDesc} />
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-10 gap-x-12">
              {[
                {
                  label: <Bilingual {...copy.registrationDetail.labelFullName} />,
                  value: registration.user?.name,
                  icon: User,
                },
                {
                  label: <Bilingual {...copy.registrationDetail.labelEmail} />,
                  value: registration.user?.email,
                  icon: Mail,
                  color: 'text-primary',
                },
                {
                  label: <Bilingual {...copy.registrationDetail.labelPhone} />,
                  value: registration.user?.phone || 'Not Provided',
                  icon: Phone,
                },
                {
                  label: <Bilingual {...copy.registrationDetail.labelGender} />,
                  value: registration.user?.gender || 'Unspecified',
                  icon: ShieldCheck,
                },
                {
                  label: <Bilingual {...copy.registrationDetail.labelProgram} />,
                  value: registration.program?.name || 'Unknown',
                  icon: GraduationCap,
                },
                {
                  label: <Bilingual {...copy.registrationDetail.labelDomisili} />,
                  value: registration.domisili || 'Not Specified',
                  icon: User,
                },
                {
                  label: <Bilingual {...copy.registrationDetail.labelReason} />,
                  value: registration.reason || 'Not Specified',
                  icon: FileText,
                  color: 'italic',
                },
              ].map((item, idx) => (
                <div key={idx} className="space-y-2">
                  <span className="text-[9px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                    {item.label}
                  </span>
                  <div className="flex items-center gap-3">
                    <item.icon size={16} className="text-outline-variant" />
                    <p
                      className={`text-base font-body font-bold ${item.color || 'text-on-surface'}`}
                    >
                      {item.value}
                    </p>
                  </div>
                </div>
              ))}
              {registration.notes && (
                <div className="col-span-1 md:col-span-2 space-y-2">
                  <span className="text-[9px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                    <Bilingual {...copy.registrationDetail.labelNotes} />
                  </span>
                  <div className="bg-surface-container-low p-6 rounded-2xl border-l-4 border-secondary/30 italic font-body text-on-surface leading-relaxed">
                    "{registration.notes}"
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Onboarding Lifecycle */}
          <section>
            <h3 className="text-xl font-headline font-bold text-on-surface mb-10 flex items-center gap-3">
              <Clock className="text-primary" size={24} />
              <Bilingual {...copy.registrationDetail.sectionOnboarding} />
            </h3>
            <div className="relative pl-14 space-y-10 before:absolute before:left-[19px] before:top-2 before:bottom-0 before:w-px before:bg-outline-variant/30">
              {[
                {
                  key: 'INITIAL',
                  title: <Bilingual {...copy.registrationDetail.stepInitial} />,
                  desc: <Bilingual {...copy.registrationDetail.stepInitialDesc} />,
                  time: new Date(registration.registeredAt).toLocaleDateString(),
                },
                {
                  key: 'WA_JOINED',
                  title: <Bilingual {...copy.registrationDetail.stepWA} />,
                  desc: <Bilingual {...copy.registrationDetail.stepWADesc} />,
                  time: registration.participantId?.whatsappGroupId
                    ? t(copy.registrationDetail.stepStatusLengkap)
                    : t(copy.registrationDetail.stepStatusTertunda),
                },
                {
                  key: 'PROFILE_COMPLETED',
                  title: <Bilingual {...copy.registrationDetail.stepProfile} />,
                  desc: <Bilingual {...copy.registrationDetail.stepProfileDesc} />,
                  time: registration.user?.gender
                    ? t(copy.registrationDetail.stepStatusTerverifikasi)
                    : t(copy.registrationDetail.stepStatusBelumLengkap),
                },
                {
                  key: 'CONSENT_SIGNED',
                  title: <Bilingual {...copy.registrationDetail.stepConsent} />,
                  desc: <Bilingual {...copy.registrationDetail.stepConsentDesc} />,
                  time: registration.consentGiven
                    ? t(copy.registrationDetail.stepStatusDitandatangani)
                    : t(copy.registrationDetail.stepStatusBelumDitandatangani),
                },
                {
                  key: 'ORIENTATION_WATCHED',
                  title: <Bilingual {...copy.registrationDetail.stepOrientation} />,
                  desc: <Bilingual {...copy.registrationDetail.stepOrientationDesc} />,
                  time:
                    registration.onboardingStatus === 'COMPLETED'
                      ? t(copy.registrationDetail.stepStatusSinkron)
                      : t(copy.registrationDetail.stepStatusMenunggu),
                },
                {
                  key: 'COMPLETED',
                  title: <Bilingual {...copy.registrationDetail.stepCompleted} />,
                  desc: <Bilingual {...copy.registrationDetail.stepCompletedDesc} />,
                  time:
                    registration.onboardingStatus === 'COMPLETED'
                      ? t(copy.registrationDetail.stepStatusAktif)
                      : t(copy.registrationDetail.stepStatusTerkunci),
                },
              ].map((step, i) => {
                const statusOrder = [
                  'INITIAL',
                  'WA_JOINED',
                  'PROFILE_COMPLETED',
                  'CONSENT_SIGNED',
                  'ORIENTATION_WATCHED',
                  'COMPLETED',
                ];
                const currentIndex = statusOrder.indexOf(
                  registration.onboardingStatus || 'INITIAL'
                );
                const stepIndex = statusOrder.indexOf(step.key);
                const isDone = stepIndex <= currentIndex;

                return (
                  <div key={i} className={`relative pb-4 ${isDone ? 'opacity-100' : 'opacity-40'}`}>
                    <div
                      className={`absolute -left-[14px] top-1 w-7 h-7 rounded-lg flex items-center justify-center border shadow-sm transition-all duration-500
                      ${isDone ? 'bg-primary text-on-primary border-primary' : 'bg-surface-container-high text-on-surface-variant border-outline-variant/30'}`}
                    >
                      {isDone ? (
                        <CheckCircle size={16} />
                      ) : (
                        <div className="w-1.5 h-1.5 rounded-full bg-current" />
                      )}
                    </div>
                    <div className="pl-8">
                      <div className="text-base font-body font-bold text-on-surface leading-none">
                        {step.title}
                      </div>
                      <p className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest mt-1.5">
                        {step.time}
                      </p>
                      <div className="text-xs font-body text-on-surface-variant mt-3 leading-relaxed max-w-sm">
                        {step.desc}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        {/* Action Panel (Span 4) */}
        <div className="lg:col-span-4 space-y-10">
          {/* Digital Credential Preview */}
          <Card className="!p-0 border border-on-surface/5 overflow-hidden shadow-level2 group">
            <div className="bg-primary p-5 flex justify-between items-center">
              <span className="text-[10px] font-label font-black text-on-primary uppercase tracking-widest">
                <Bilingual {...copy.registrationDetail.sectionProtocol} />
              </span>
              <ShieldCheck size={16} className="text-on-primary/60" />
            </div>
            <div className="p-10 flex flex-col items-center">
              <div className="w-40 h-52 bg-surface-container-high rounded-2xl overflow-hidden mb-8 border-4 border-white shadow-inner flex items-center justify-center">
                <User size={80} className="text-on-surface-variant/20" />
              </div>
              <h4 className="text-2xl font-headline font-bold text-on-surface m-0 text-center">
                {registration.user?.name}
              </h4>
              <p className="text-[11px] font-label font-black text-primary uppercase tracking-[0.25em] mt-2 mb-10">
                <Bilingual {...copy.registrationDetail.labelCandidate} />
              </p>

              <div className="w-full space-y-4 py-6 border-y border-outline-variant/10">
                <div className="flex justify-between items-center">
                  <span className="text-[9px] font-label font-bold text-on-surface-variant uppercase">
                    <Bilingual {...copy.registrationDetail.labelCredential} />
                  </span>
                  <span className="text-xs font-mono font-black">
                    {registration.participantId?.participantCode ||
                      `#BID-${registration.id.slice(-6).toUpperCase()}`}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[9px] font-label font-bold text-on-surface-variant uppercase">
                    <Bilingual {...copy.registrationDetail.labelValidity} />
                  </span>
                  <span className="text-xs font-body font-bold">
                    {registration.program?.startDate
                      ? new Date(registration.program.startDate).getFullYear()
                      : 'TBD'}{' '}
                    —{' '}
                    {registration.program?.endDate
                      ? new Date(registration.program.endDate).getFullYear()
                      : 'TBD'}
                  </span>
                </div>
              </div>

              <div className="mt-10 p-5 bg-surface-container-low rounded-2xl opacity-60 grayscale group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700">
                <div className="w-20 h-20 bg-primary/10 rounded flex items-center justify-center">
                  <FileText size={40} className="text-primary/20" />
                </div>
              </div>

              <Button
                onClick={() => setIsReceiptModalOpen(true)}
                variant="ghost"
                className="w-full mt-10 rounded-xl border border-outline-variant/30 text-[10px] uppercase font-black tracking-widest gap-2"
              >
                <DownloadIcon size={14} />{' '}
                <Bilingual {...copy.registrationDetail.btnDownloadProof} />
              </Button>
            </div>
          </Card>

          {/* Communication Hub */}
          <Card className="border-l-4 border-secondary/50">
            <div className="flex items-center gap-4 mb-8">
              <div className="p-3 bg-secondary/10 rounded-xl text-secondary">
                <MessageSquare size={20} />
              </div>
              <div>
                <h3 className="text-sm font-headline font-bold text-on-surface m-0">
                  <Bilingual {...copy.registrationDetail.sectionCommunication} />
                </h3>
                <p className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                  <Bilingual {...copy.registrationDetail.communicationDesc} />
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-5 bg-surface-container-low rounded-2xl border border-on-surface/5">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase opacity-60">
                    <Bilingual {...copy.registrationDetail.labelPlacementStatus} />
                  </span>
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[10px] font-mono font-bold ${registration.participantId?.whatsappGroupId ? 'text-success' : 'text-secondary'}`}
                    >
                      {registration.participantId?.whatsappGroupId ? (
                        <Bilingual {...copy.registrationDetail.statusAssigned} />
                      ) : (
                        <Bilingual {...copy.registrationDetail.statusUnassigned} />
                      )}
                    </span>
                    {registration.participantId?.whatsappGroupId && (
                      <Link
                        to={`/whatsapp-groups/${registration.participantId.whatsappGroupId}`}
                        className="text-[9px] font-label font-black text-primary hover:underline uppercase tracking-tighter"
                      >
                        {t(copy.registrationDetail.inspect)}
                      </Link>
                    )}
                  </div>
                </div>
                <p className="text-xs font-body text-on-surface-variant m-0 leading-snug">
                  {registration.participantId?.whatsappGroupId
                    ? t(copy.registrationDetail.msgAssigned).replace(
                        '{name}',
                        waGroups?.find(g => g.id === registration.participantId?.whatsappGroupId)
                          ?.name || 'Main cohort'
                      )
                    : t(copy.registrationDetail.msgUnassigned)}
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2">
                  <Bilingual {...copy.registrationDetail.labelSelectWAChannel} />
                </label>
                <select
                  className="w-full bg-surface-container-high border-none rounded-xl p-3 text-sm font-body outline-none focus:ring-2 focus:ring-primary/20"
                  value={selectedGroupId || registration.participantId?.whatsappGroupId || ''}
                  onChange={e => setSelectedGroupId(e.target.value)}
                >
                  <option value="">{t(copy.registrationDetail.optionSelectChannel)}</option>
                  {waGroups
                    ?.filter(g => {
                      const userGender = registration.user?.gender;
                      const isNotFull = (g._count?.members || 0) < g.maxMembers;
                      if (!isNotFull && g.id !== registration.participantId?.whatsappGroupId)
                        return false;

                      if (!userGender) return g.gender === 'MIXED';
                      const targetWA = userGender === 'MALE' ? 'IKHWAN' : 'AKHWAT';
                      return g.gender === targetWA || g.gender === 'MIXED';
                    })
                    .map(g => (
                      <option key={g.id} value={g.id}>
                        {g.name} ({g.gender}) — {g._count?.members || 0}/{g.maxMembers}
                      </option>
                    ))}
                </select>
              </div>
              <Button
                onClick={handleAssign}
                disabled={
                  !selectedGroupId ||
                  selectedGroupId === registration?.participantId?.whatsappGroupId ||
                  assignMutation.isPending
                }
                className="w-full rounded-xl text-xs uppercase tracking-widest font-bold shadow-level1"
              >
                {assignMutation.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Bilingual {...copy.registrationDetail.btnAssignSubmit} />
                )}
              </Button>
            </div>
          </Card>

          {/* System Flags */}
          <div className="bg-surface-container-low p-6 rounded-2xl space-y-4 border border-on-surface/5">
            <h4 className="text-[10px] font-label font-bold text-primary uppercase tracking-[0.2em] mb-4">
              <Bilingual {...copy.registrationDetail.sectionInternalAudit} />
            </h4>
            <div className="space-y-2 pb-2">
              <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2">
                <Bilingual {...copy.registrationDetail.labelVerifyNotes} />
              </label>
              <textarea
                className="w-full bg-surface-container-high border-none rounded-xl p-3 text-sm font-body outline-none focus:ring-2 focus:ring-primary/20 resize-none h-20 shadow-inner"
                placeholder={t(copy.registrationDetail.placeholderVerifyNotes)}
                value={adminNotes}
                onChange={e => setAdminNotes(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <Button
                variant="outline"
                size="sm"
                className="w-full text-[10px] opacity-50 cursor-not-allowed"
              >
                <Bilingual {...copy.registrationDetail.btnLogAudit} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full text-[10px] opacity-50 cursor-not-allowed"
              >
                <Bilingual {...copy.registrationDetail.btnExport} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full text-[10px] opacity-50 cursor-not-allowed"
              >
                <Bilingual {...copy.registrationDetail.btnInspect} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full text-[10px] opacity-50 cursor-not-allowed italic"
              >
                <Bilingual {...copy.registrationDetail.btnFutureActions} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Audit Footnote */}
      <footer className="pt-12 border-t border-outline-variant/10 flex flex-col md:flex-row justify-between items-center gap-6 opacity-40">
        <p className="text-[10px] font-body font-medium m-0 tracking-wide">
          Executive Access Log:{' '}
          {(() => {
            const firstRole = admin?.roles?.[0];
            if (typeof firstRole === 'object' && firstRole !== null && 'role' in firstRole) {
              return (firstRole as { role: { name: string } }).role.name;
            }
            return (firstRole as string) || 'Administrator';
          })()}{' '}
          —{' '}
          {t(copy.participantDetail.auditLog)
            .replace('{admin}', admin?.name || '')
            .replace(
              '{date}',
              new Date().toLocaleDateString(language === 'ar' ? 'ar-EG' : 'id-ID')
            )}
        </p>
        <div className="flex gap-8"></div>
      </footer>

      {isReceiptModalOpen && registration && (
        <ReceiptModal receiptData={registration} onClose={() => setIsReceiptModalOpen(false)} />
      )}
      <RejectionModal
        isOpen={isRejectionModalOpen}
        onClose={() => setIsRejectionModalOpen(false)}
        onConfirm={handleReject}
        isPending={rejectMutation.isPending}
        participantName={registration.user?.name || ''}
      />
      {editUserContext && (
        <EditUserModal
          userId={editUserContext.userId}
          missingFields={editUserContext.missingFields}
          reason={editUserContext.reason}
          primaryActionLabel={copy.users.btnSaveAndApprove}
          onClose={() => setEditUserContext(null)}
          onSaved={() => {
            const registrationId = editUserContext.registrationId;
            setEditUserContext(null);
            approveMutation.mutate(
              { id: registrationId, adminNotes: adminNotes || 'Criteria met' },
              {
                onSuccess: () => {
                  void navigate('/registrations');
                },
                onError: err => {
                  if (isIncompleteUserProfileError(err)) {
                    setEditUserContext({
                      userId: err.body.userId,
                      registrationId: err.body.registrationId,
                      missingFields: err.body.missingFields,
                      reason: err.body?.message || err.message,
                    });
                  }
                },
              }
            );
          }}
        />
      )}
    </div>
  );
}
